import clsx from 'clsx';
import type { ComponentPropsWithRef, ReactNode } from 'react';
import { forwardRef, useEffect, useState } from 'react';
import type { FieldError } from 'react-hook-form';
import type { FieldValidationError } from '../../types/cloud_api_error_type';
import { FormErrorMessage } from './FormErrorMessage';
import { Tooltip } from './Tooltip';

type InputProps = Omit<ComponentPropsWithRef<'input'>, 'className'> & {
  label?: string | ReactNode;
  tooltip?: ReactNode;
  state?: 'valid' | 'warning' | 'invalid';
  fieldError?: FieldError;
  supplement?: string;
  validationError?: FieldValidationError;
  classNames?: Partial<{
    root: string;
    labelContainer: string;
    label: string;
    input: string;
  }>;
};

export const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const {
    label,
    tooltip,
    state,
    fieldError,
    classNames,
    supplement,
    validationError,
    type,
    ...inputProps
  } = props;
  const [tooltipElement, setTooltipElement] = useState<ReactNode | null>(null);
  const [isRevealPassword, setIsRevealPassword] = useState(false);
  const [currentType, setCurrentType] = useState(type);

  useEffect(() => {
    setTooltipElement(tooltip);
  }, [tooltip]);

  const togglePassword = () => {
    const isPassword = currentType === 'password';
    setIsRevealPassword((prevState) => !prevState);
    setCurrentType(isPassword ? 'text' : 'password');
  };

  return (
    <>
      <div className={clsx('form-group', classNames?.root)}>
        {label && (
          <div className={classNames?.labelContainer}>
            <label
              className={clsx('form-control-label', classNames?.label)}
              htmlFor={inputProps.id}
            >
              {label}
              {tooltipElement}
            </label>
            {inputProps.required && (
              <span className="tx-danger ml-1" aria-hidden>
                *
              </span>
            )}
          </div>
        )}
        <div className={'c-input-box'}>
          <input
            ref={ref}
            {...inputProps}
            type={currentType}
            className={clsx(
              'form-control',
              state && `is-${state}`,
              type === 'password' && 'pd-r-30',
              classNames?.input
            )}
          />
          {supplement && (
            <small className="form-text text-muted">{supplement}</small>
          )}
          {type === 'password' && (
            <span
              onClick={togglePassword}
              role="presentation"
              className={'c-password-reveal'}
            >
              {isRevealPassword ? (
                <Tooltip
                  toggle="info"
                  placement="top"
                  text="パスワードを非表示にします"
                >
                  <i className="fa fa-eye-slash fa-lg" />
                </Tooltip>
              ) : (
                <Tooltip
                  toggle="info"
                  placement="top"
                  text="パスワードを表示します"
                >
                  <i className="fa fa-eye fa-lg" />
                </Tooltip>
              )}
            </span>
          )}
          <FormErrorMessage formError={fieldError} />
          <FormErrorMessage formError={validationError} />
        </div>
      </div>
      <style>
        {`
          .mg-b-minus-8 { margin-bottom: -8px; }

          .c-input-box {
            position: relative;
          }

          .c-password-reveal {
            left: auto;
            position: absolute;
            right: 10px;
            top: 10px;
            cursor: pointer;
            color: black;
          }
        `}
      </style>
    </>
  );
});
Input.displayName = '@signate-cloud/Input';
